import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  Users,
  Briefcase,
  LifeBuoy,
  Percent,
  Ticket,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  DollarSign,
  Bell,
  Megaphone,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  Smartphone,
  FileText,
  Palette,
  Images,
  Star,
  Wifi,
  HelpCircle,
  BarChart3,
  Clock,
  History,
  RefreshCw,
  Shield,
  FolderTree,
  Truck,
  LayoutGrid,
  ClipboardList,
  Calendar,
  Wrench,
  Package,
  MapPin,
  UserCheck,
  Locate,
  Car,
  Wallet,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useMemo } from 'react';
import { useAuthStore, type UserRole } from '@/store/authStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useBrandingTheme } from '@/hooks/useBrandingTheme';
import { useProviderCapabilities } from '@/hooks/useProviderCapabilities';
import { isProviderPathAllowed } from '@/lib/provider-capabilities.mjs';
import { useEnabledModules } from '@/hooks/useEnabledModules';
import { requiredModuleForPath } from '@/lib/moduleNav';
import { useUIStore } from '@/store/uiStore';

interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  href?: string;
  children?: { icon: React.ComponentType<{ className?: string }>; labelKey: string; href: string }[];
}

// SuperAdmin menu - Platform-level management
// Note: Tenant Admins and Subscriptions are managed inside Tenant Details
const superAdminItems: SidebarItem[] = [
  { icon: LayoutDashboard, labelKey: 'layout.sidebar.platformDashboard', href: '/' },
  { icon: Building2, labelKey: 'layout.sidebar.tenants', href: '/tenants' },
  { icon: BarChart3, labelKey: 'layout.sidebar.analytics', href: '/analytics' },
  {
    icon: FileText,
    labelKey: 'layout.sidebar.platformData',
    children: [
      { icon: Building2, labelKey: 'layout.sidebar.cities', href: '/cities' },
      { icon: Users, labelKey: 'layout.sidebar.nationalities', href: '/nationalities' },
    ],
  },
  { icon: Bell, labelKey: 'layout.sidebar.notificationSettings', href: '/notification-settings' },
  { icon: Settings, labelKey: 'layout.sidebar.platformSettings', href: '/platform-settings' },
];

// TenantAdmin menu - Grouped by domain for clean navigation
const tenantAdminItems: SidebarItem[] = [
  // ── Overview ──
  { icon: LayoutDashboard, labelKey: 'layout.sidebar.dashboard', href: '/' },

  // ── Orders ──
  { icon: ShoppingCart, labelKey: 'layout.sidebar.orders', href: '/orders' },

  // ── People ──
  {
    icon: Users,
    labelKey: 'layout.sidebar.people',
    children: [
      { icon: Users, labelKey: 'layout.sidebar.customers', href: '/customers' },
      { icon: Briefcase, labelKey: 'layout.sidebar.providers', href: '/providers' },
      { icon: ShieldCheck, labelKey: 'layout.sidebar.providerApproval', href: '/providers/approval' },
      { icon: Wifi, labelKey: 'layout.sidebar.providerStatus', href: '/providers/status' },
      { icon: CreditCard, labelKey: 'layout.sidebar.debts', href: '/providers/debts' },
    ],
  },

  // ── Catalog ──
  {
    icon: FolderTree,
    labelKey: 'layout.sidebar.catalogManagement',
    children: [
      { icon: FolderTree, labelKey: 'layout.sidebar.categories', href: '/categories' },
      { icon: Wrench, labelKey: 'layout.sidebar.services', href: '/services' },
      { icon: ShoppingBag, labelKey: 'layout.sidebar.products', href: '/products' },
      { icon: Truck, labelKey: 'layout.sidebar.dispatchers', href: '/dispatchers' },
    ],
  },

  // ── Finance ──
  {
    icon: DollarSign,
    labelKey: 'layout.sidebar.finance',
    children: [
      { icon: DollarSign, labelKey: 'layout.sidebar.transactions', href: '/transactions' },
      { icon: Star, labelKey: 'layout.sidebar.reviews', href: '/reviews' },
    ],
  },

  // ── Support ──
  {
    icon: LifeBuoy,
    labelKey: 'layout.sidebar.support',
    children: [
      { icon: LifeBuoy, labelKey: 'layout.sidebar.tickets', href: '/tickets' },
      { icon: Clock, labelKey: 'layout.sidebar.supportHours', href: '/support-hours' },
      { icon: HelpCircle, labelKey: 'layout.sidebar.faqs', href: '/faqs' },
    ],
  },

  // ── Maps ──
  {
    icon: MapPin,
    labelKey: 'layout.sidebar.maps',
    children: [
      { icon: MapPin, labelKey: 'layout.sidebar.ordersMap', href: '/maps/orders' },
      { icon: Users, labelKey: 'layout.sidebar.providersMap', href: '/maps/providers' },
      { icon: Locate, labelKey: 'layout.sidebar.nearby', href: '/maps/nearby' },
    ],
  },

  // ── Marketing ──
  {
    icon: Megaphone,
    labelKey: 'layout.sidebar.marketing',
    children: [
      { icon: Ticket, labelKey: 'layout.sidebar.coupons', href: '/coupons' },
      { icon: Percent, labelKey: 'layout.sidebar.offers', href: '/offers' },
      { icon: Bell, labelKey: 'layout.sidebar.notifications', href: '/notifications' },
    ],
  },

  // ── Notifications ──
  {
    icon: Bell,
    labelKey: 'layout.sidebar.notificationManagement',
    children: [
      { icon: Bell, labelKey: 'layout.sidebar.notificationSettings', href: '/notification-settings' },
      { icon: History, labelKey: 'layout.sidebar.notificationLogs', href: '/notification-logs' },
    ],
  },

  // ── Branding ──
  {
    icon: Palette,
    labelKey: 'layout.sidebar.branding',
    children: [
      { icon: Palette, labelKey: 'layout.sidebar.brandIdentity', href: '/branding' },
      { icon: Images, labelKey: 'layout.sidebar.welcomeSliders', href: '/branding/sliders' },
    ],
  },

  // ── System ──
  {
    icon: Settings,
    labelKey: 'layout.sidebar.system',
    children: [
      { icon: Settings, labelKey: 'layout.sidebar.systemConfig', href: '/config' },
      { icon: Smartphone, labelKey: 'layout.sidebar.appVersions', href: '/app-versions' },
      { icon: RefreshCw, labelKey: 'layout.sidebar.recurringJobs', href: '/recurring-jobs' },
      { icon: Shield, labelKey: 'layout.sidebar.usersSecurity', href: '/users-security' },
      { icon: Settings, labelKey: 'layout.sidebar.settings', href: '/settings' },
    ],
  },
];

// ProviderAdmin menu - the master list. Actual sidebar is filtered by the
// provider's additive capabilities and tenant module/channel entitlements.
const providerAdminItems: SidebarItem[] = [
  { icon: LayoutDashboard, labelKey: 'providerAdmin.dashboard', href: '/business' },
  { icon: ClipboardList, labelKey: 'providerAdmin.orders', href: '/business/orders' },
  { icon: ShoppingBag, labelKey: 'providerAdmin.shopOrders', href: '/business/shop-orders' },
  { icon: RotateCcw, labelKey: 'providerAdmin.returns', href: '/business/returns' },
  { icon: Users, labelKey: 'providerAdmin.team', href: '/business/team' },
  { icon: Calendar, labelKey: 'providerAdmin.calendar', href: '/business/calendar' },
  { icon: Wrench, labelKey: 'providerAdmin.services', href: '/business/services' },
  { icon: Package, labelKey: 'providerAdmin.products', href: '/business/products' },
  // Unified workbench (BZ3, 2026-05-23) — replaces the previous three
  // separate items: dispatch, fleet (live map), and board (kanban).
  { icon: LayoutGrid, labelKey: 'providerAdmin.operations', href: '/business/operations' },
  { icon: MapPin, labelKey: 'providerAdmin.zones', href: '/business/zones' },
  { icon: Car, labelKey: 'providerAdmin.vehicles', href: '/business/vehicles' },
  { icon: Wallet, labelKey: 'providerAdmin.wallets', href: '/business/wallets' },
  { icon: BarChart3, labelKey: 'providerAdmin.reports', href: '/business/reports' },
  // BZ7 (2026-05-24): driver scorecards. Visible to team segment only — solo / salon / shop
  // segments don't have drivers, so the page would render an empty state.
  { icon: BarChart3, labelKey: 'providerAdmin.scorecards', href: '/business/scorecards' },
  { icon: DollarSign, labelKey: 'providerAdmin.finance', href: '/business/finance' },
  // BZ8 (2026-05-24): cash-on-delivery reconciliation. Shown for shops + team
  // (drivers carry cash); salon and solo segments rarely run cash-collection workflows.
  { icon: DollarSign, labelKey: 'providerAdmin.cashReconciliation', href: '/business/cash-reconciliation' },
  { icon: UserCheck, labelKey: 'providerAdmin.customers', href: '/business/customers' },
  { icon: Settings, labelKey: 'providerAdmin.settings', href: '/business/settings' },
];

/**
 * Filter the master provider-admin sidebar to what actually applies to
 * this owner's business. A solo plumber doesn't need /vehicles or
 * /shop-orders; a salon doesn't need /fleet or /vehicles; a shop doesn't
 * need /calendar or /zones. Cuts the visible nav by 30-50% per segment.
 *
 * Hrefs are the contract — we filter by URL path so renames in `labelKey`
 * don't break this logic.
 */
function filterProviderAdminItems(
  items: SidebarItem[],
  capabilities: Record<string, unknown>,
  ready: boolean,
): SidebarItem[] {
  const safeBeforeResolution = new Set(['/business', '/business/settings']);
  return items.filter((item) => item.href && (
    ready
      ? isProviderPathAllowed(item.href, capabilities)
      : safeBeforeResolution.has(item.href)
  ));
}

/**
 * Hide tenant-admin nav entries whose module is disabled for this tenant, so
 * admins don't land on a page the backend will 403. Drops module-gated leaf
 * items and any parent group left empty. Cross-cutting items (no module
 * mapping) always pass.
 */
function filterTenantItemsByModules(
  items: SidebarItem[],
  isManageable: (moduleKey: string) => boolean,
): SidebarItem[] {
  const leafAllowed = (href?: string) => {
    const moduleKey = href ? requiredModuleForPath(href) : undefined;
    return !moduleKey || isManageable(moduleKey);
  };

  return items
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((child) => leafAllowed(child.href)) }
        : item,
    )
    .filter((item) =>
      item.children ? item.children.length > 0 : leafAllowed(item.href),
    );
}

function filterTenantItemsByRole(items: SidebarItem[], role: UserRole): SidebarItem[] {
  if (role !== 'support') return items;

  return items
    .map((item) => item.children
      ? { ...item, children: item.children.filter((child) => child.href !== '/notification-settings') }
      : item)
    .filter((item) => item.children ? item.children.length > 0 : true);
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { logout } = useAuthStore();
  const { language } = useUIStore();
  const { role } = useAuthStore();
  const { isSuperAdmin, isImpersonating } = useTenantContext();
  const isProviderAdmin = role === 'provideradmin';
  const { logoUrl, appNameArabic } = useBrandingTheme();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['layout.sidebar.marketing']);

  const providerCapabilities = useProviderCapabilities();

  // Tenant's enabled modules — hide tenant-admin items for disabled modules.
  const { isManageable, ready: modulesReady } = useEnabledModules();

  // Select menu based on role - completely separate menus per role
  const sidebarItems = useMemo(
    () => {
      if (isProviderAdmin) return filterProviderAdminItems(
        providerAdminItems,
        providerCapabilities.capabilities,
        providerCapabilities.ready,
      );
      // SuperAdmin on the platform menu (not scoped to a tenant).
      if (isSuperAdmin && !isImpersonating) return superAdminItems;
      // Impersonating SuperAdmin OR real tenant admin: show the tenant menu,
      // gated by that tenant's enabled modules. Filter only once the module
      // list is known, to avoid a flash of hidden items on first load.
      const moduleFilteredItems = modulesReady
        ? filterTenantItemsByModules(tenantAdminItems, isManageable)
        : tenantAdminItems;
      return filterTenantItemsByRole(moduleFilteredItems, role);
    },
    [isSuperAdmin, isImpersonating, isProviderAdmin, providerCapabilities.capabilities, providerCapabilities.ready, modulesReady, isManageable, role]
  );

  const toggleSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
    onClose?.();
  };

  const toggleSection = (labelKey: string) => {
    setExpandedSections((prev) =>
      prev.includes(labelKey) ? prev.filter((l) => l !== labelKey) : [...prev, labelKey]
    );
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const sidebarOpen = isOpen || isMobileOpen;

  const isChildActive = (children: SidebarItem['children']) => {
    if (!children) return false;
    return children.some(
      (child) =>
        location.pathname === child.href ||
        (child.href !== '/' && location.pathname.startsWith(child.href))
    );
  };

  const CollapseIcon = language === 'ar' ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Mobile Trigger */}
      <div className="md:hidden fixed top-4 ltr:left-4 rtl:right-4 z-50">
        <Button variant="outline" size="icon" onClick={toggleSidebar}>
          {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => {
            setIsMobileOpen(false);
            onClose?.();
          }}
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={cn(
          'fixed top-0 h-full w-[270px] bg-sidebar text-sidebar-foreground z-40 transition-transform duration-300 ease-in-out border-sidebar-border shadow-xl',
          'ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l',
          // Always visible on desktop (md+), hide/show on mobile based on sidebarOpen
          'md:translate-x-0',
          !sidebarOpen && 'max-md:ltr:-translate-x-full max-md:rtl:translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-[60px] flex items-center px-6 border-b border-sidebar-border gap-3">
            {(!isSuperAdmin || isImpersonating) && !isProviderAdmin && logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-8 w-8 rounded object-cover" />
            ) : null}
            <h1 className="text-xl font-bold text-white tracking-tight">
              {isProviderAdmin ? (
                <>{t('providerAdmin.myBusiness')}</>
              ) : (!isSuperAdmin || isImpersonating) && appNameArabic ? (
                appNameArabic
              ) : isSuperAdmin ? (
                <>Tanazom <span className="text-[hsl(160,100%,40%)]">Platform</span></>
              ) : (
                <>Tanazom <span className="text-primary">Dashboard</span></>
              )}
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
            {sidebarItems.map((item) => {
              if (item.children) {
                const isExpanded = expandedSections.includes(item.labelKey);
                const hasActiveChild = isChildActive(item.children);

                return (
                  <div key={item.labelKey}>
                    <button
                      onClick={() => toggleSection(item.labelKey)}
                      className={cn(
                        'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mb-1',
                        hasActiveChild
                          ? 'bg-sidebar-accent/50 text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon
                          className={cn(
                            'h-5 w-5',
                            hasActiveChild ? 'text-primary' : 'text-muted-foreground'
                          )}
                        />
                        {t(item.labelKey)}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <CollapseIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>

                    {/* Sub-items */}
                    {isExpanded && (
                      <div className="ltr:ml-4 ltr:pl-4 ltr:border-l rtl:mr-4 rtl:pr-4 rtl:border-r border-sidebar-border space-y-1">
                        {item.children.map((child) => {
                          const isChildItemActive =
                            location.pathname === child.href ||
                            (child.href !== '/' && location.pathname.startsWith(child.href));
                          return (
                            <NavLink
                              key={child.href}
                              to={child.href}
                              onClick={() => {
                                setIsMobileOpen(false);
                                onClose?.();
                              }}
                              className={cn(
                                'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                                isChildItemActive
                                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                              )}
                            >
                              <child.icon
                                className={cn(
                                  'h-4 w-4',
                                  isChildItemActive ? 'text-primary' : 'text-muted-foreground'
                                )}
                              />
                              {t(child.labelKey)}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive =
                location.pathname === item.href ||
                (item.href !== '/' && location.pathname.startsWith(item.href!));
              return (
                <NavLink
                  key={item.href}
                  to={item.href!}
                  onClick={() => {
                    setIsMobileOpen(false);
                    onClose?.();
                  }}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mb-1',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                  )}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5',
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    )}
                  />
                  {t(item.labelKey)}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t border-sidebar-border">
            <Button
              variant="ghost"
              className="w-full justify-start text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={handleLogout}
            >
              <LogOut className="me-2 h-4 w-4" />
              {t('layout.sidebar.logout')}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}
